import React, { useContext, useMemo, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { RightsContext } from 'providers/RightsContext';
import { get, set } from '@totalsoft/react-state-lens';
import { onCheckBoxChange } from 'utils/propertyChangeAdapters';
import { FormControlLabel, Grid, Switch } from '@mui/material';
import MuiAutocomplete from '@mui/material/Autocomplete';
import MuiTextField from '@mui/material/TextField';
import { Button, Typography, IconButton, TextField } from '@totalsoft/rocket-ui';
import ClearIcon from '@mui/icons-material/ClearOutlined';
import ParentCommentComponent from './ParentCommentComponent';
import { isValid, getErrors } from '@totalsoft/pure-validations-react';
import { Box } from '@mui/material';
import { useEffect } from 'react';
import { defaultComment } from '../../constants/defaultComment';

function AddCommentComponent({
  saving,
  commentLens,
  onSave,
  onAddRecipient,
  onResetComment,
  fullScreen,
  userAgendaLens,
  validationInfo,
  refetchComments,
  inputRef,
  dirtyComment,
  setDirtyComment
}) {
  const { t, i18n } = useTranslation();
  const { addDocumentComments, internalDocumentComments } = useContext(RightsContext);
  const footerRef = useRef();
  const comment = commentLens |> get;
  const switchRef = useRef();

  useEffect(() => {
    if (!comment || comment === defaultComment) return;
    setDirtyComment(true);
  }, [comment, setDirtyComment]);

  const users = useMemo(
    () =>
      (get(userAgendaLens) || [])
        .filter(option => option.email || option.userGroupId != null)
        .map(agenda => ({
          ...agenda,
          name: agenda.contactName || agenda.email,
          role: agenda.role ? agenda.role.split('&').map(role => role.trim()) : null
        }))
        .map(agenda => ({
          ...agenda,
          display: `${agenda.contactName || agenda.email}${
            agenda.role?.length > 0
              ? ` (${agenda.role
                  .map(role => {
                    return i18n.exists(`Offer.Comments.${role}`) ? t(`Offer.Comments.${role}`) : role;
                  })
                  .join(' & ')})`
              : ''
          }`
        })),
    [userAgendaLens, t, i18n]
  );

  const comments = useMemo(
    () =>
      (comment?.recipients || []).map(comment => ({
        ...comment,
        name: comment.contactName || comment.email
      })),
    [comment]
  );

  const renderUserAgendaOption = useCallback(
    (props, option) => {
      return (
        <li {...props}>
          <Box px={1} py={0.5}>
            <span
              style={{
                fontWeight: option.isStandardContact ? 700 : 400
              }}
            >
              {option.contactName ? option.contactName : option.email}
              {option.role && (
                <span>
                  (
                  {option.role
                    ? option.role
                        .map(role => {
                          return i18n.exists(`Offer.Comments.${role}`) ? t(`Offer.Comments.${role}`) : role;
                        })
                        .join(' & ')
                    : option.role}
                  )
                </span>
              )}
            </span>
          </Box>
        </li>
      );
    },
    [i18n, t]
  );

  const handleReset = () => {
    onResetComment(), refetchComments();
  };
  return (
    <Grid container spacing={2} ref={footerRef}>
      {comment.parentCommentId && (
        <Grid item xs={12}>
          <ParentCommentComponent comment={comment.parentComment} />
        </Grid>
      )}
      <Grid item xs={12}>
        <TextField
          required
          fullWidth
          multiline
          variant='outlined'
          value={comment?.commentText || ''}
          onChange={commentLens.commentText |> set}
          placeholder={t('Offer.Comments.AddComment')}
          disabled={saving || !addDocumentComments}
          error={!isValid(validationInfo.commentText)}
          helperText={getErrors(validationInfo.commentText)}
          inputRef={inputRef}
        />
      </Grid>
      {dirtyComment && (
        <>
          <Grid item xs={12}>
            <Typography fontWeight='bold' color='primary'>
              {t('Offer.Comments.SendEmailTo')}
            </Typography>
            <MuiAutocomplete
              id='userAgenda'
              multiple
              fullWidth
              options={users}
              value={comments}
              disabled={saving}
              onChange={(_event, newValue) => onAddRecipient(newValue)}
              getOptionLabel={option => option.display || option.name || option.email}
              renderOption={renderUserAgendaOption}
              freeSolo
              renderInput={params => <MuiTextField {...params} variant='outlined' placeholder={t('Offer.Comments.AddMail')} />}
            />
          </Grid>
          <Grid item xs={12}>
            <Grid container alignItems='center'>
              {internalDocumentComments && (
                <Grid item xs='auto'>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={comment?.internal || false}
                        onChange={commentLens.internal |> set |> onCheckBoxChange}
                        disabled={comment.parentComment?.internal ? true : saving}
                      />
                    }
                    label={t('Offer.Comments.InternalCheck')}
                    inputRef={switchRef}
                  />
                </Grid>
              )}
              <Grid item xs>
                <Grid container spacing={1} alignItems='center' justifyContent='space-between'>
                  <Grid item xs style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                      id={'addComment_saveBtn'}
                      color='primary'
                      variant='contained'
                      fullWidth
                      onClick={onSave}
                      disabled={!dirtyComment || saving}
                      style={{ maxWidth: fullScreen ? '100px' : '' }}
                    >
                      {saving ? t('Offer.Comments.Sending') : t('Offer.Comments.Send')}
                    </Button>
                  </Grid>
                  <Grid item>
                    <IconButton
                      id={'addComment_clearBtn'}
                      color='primary'
                      variant='text'
                      size='small'
                      tooltip={t('Offer.Comments.Clear')}
                      onClick={handleReset}
                      disabled={!dirtyComment || saving}
                    >
                      <ClearIcon />
                    </IconButton>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </>
      )}
    </Grid>
  );
}

AddCommentComponent.propTypes = {
  saving: PropTypes.bool.isRequired,
  commentLens: PropTypes.object.isRequired,
  onSave: PropTypes.func.isRequired,
  onAddRecipient: PropTypes.func.isRequired,
  onResetComment: PropTypes.func.isRequired,
  fullScreen: PropTypes.bool.isRequired,
  userAgendaLens: PropTypes.object.isRequired,
  validationInfo: PropTypes.object.isRequired,
  refetchComments: PropTypes.func.isRequired,
  inputRef: PropTypes.object.isRequired,
  dirtyComment: PropTypes.bool.isRequired,
  setDirtyComment: PropTypes.func.IsRequired
};

export default AddCommentComponent;
